import { parseISO } from "date-fns";

import { TradeCommand, TradeCommandType } from "../../../../services/holdings";
import { DateFormat, formatDate } from "../../../../utils";
import { ACCOUNT_LEVEL_POSITION, CHARGE_KIND_TO_NOUN } from "../../constants";
import {
  ADJUSTMENT_DIRECTION_GROUP_LABEL,
  CHARGE_FIELD_NAMES,
  INTEREST_DIRECTION_GROUP_LABEL,
} from "../constants";
import { ChargeNoun } from "../../types";
import { ChargeFieldName, InterestDirection, TaxedIncome } from "../types";
import { isSettlementType } from "../../utils";
import { recordedCashbackValuesOf } from "../cashbackFunding";
import { centsOf, settlementNetCentsOf } from "../settlementNote";
import {
  TradeCorrectionCopy,
  TradeCorrectionCopyMap,
  TradeCorrectionFormValues,
} from "./types";

export const FORM_ID = "trade-correction";

// The correction replaces every row the original submission wrote, so an empty field removes the charge event entirely — every type's wording has to say so, which is why the noun is the only part that varies.
const chargeHelperFor = (noun: string): string =>
  `The commission charged on this ${noun}. Clearing it removes the fee from the ${noun}.`;

const TRADE_CONTEXT_HINT =
  "The trade type and the position it belongs to can't be changed. To move a trade elsewhere, delete it and record it again.";

const ACCOUNT_CONTEXT_HINT =
  "The entry type can't be changed, and this entry belongs to the account rather than to a position. To record it differently, delete it and enter it again.";

/** The wording every non-correctable type falls back to. */
const genericCorrectionCopy = (
  noun: string,
  amountHelper: string,
): TradeCorrectionCopy => ({
  eyebrow: `CORRECT ${noun.toUpperCase()}`,
  ariaLabel: `Correct ${noun} form`,
  submitLabel: "Save changes",
  compactActionLabel: "Save",
  compactTitle: `Correct ${noun}`,
  amountHelper,
  chargeHelper: chargeHelperFor(noun),
  headerNoun: "trade",
  contextHint: TRADE_CONTEXT_HINT,
  discardTitle: "Cancel this correction?",
});

const accountLevelCorrectionCopy = (
  noun: string,
  amountHelper: string,
): TradeCorrectionCopy => ({
  ...genericCorrectionCopy(noun, amountHelper),
  headerNoun: "account entry",
  contextHint: ACCOUNT_CONTEXT_HINT,
});

// The map remains total over wire types even though only correctable commands reach the panel.
export const TRADE_CORRECTION_COPY: TradeCorrectionCopyMap = {
  BUY_SETTLEMENT: {
    eyebrow: "CORRECT PURCHASE",
    ariaLabel: "Correct purchase form",
    submitLabel: "Save changes",
    compactActionLabel: "Save",
    compactTitle: "Correct purchase",
    amountHelper: "Amount paid for the units. Any fee is charged separately.",
    chargeHelper: chargeHelperFor("purchase"),
    headerNoun: "trade",
    contextHint: TRADE_CONTEXT_HINT,
    discardTitle: "Cancel this correction?",
  },
  SELL_SETTLEMENT: {
    eyebrow: "CORRECT SALE",
    ariaLabel: "Correct sale form",
    submitLabel: "Save changes",
    compactActionLabel: "Save",
    compactTitle: "Correct sale",
    // The amount is the gross proceeds and the fee is deducted from it, so the
    // copy has to say which of the two figures this field is.
    amountHelper:
      "What the sale raised, before the broker's commission. Any commission is the fee below.",
    chargeHelper: chargeHelperFor("sale"),
    headerNoun: "trade",
    contextHint: TRADE_CONTEXT_HINT,
    discardTitle: "Cancel this correction?",
  },
  DIVIDEND: {
    eyebrow: "CORRECT DIVIDEND",
    ariaLabel: "Correct dividend form",
    submitLabel: "Save changes",
    compactActionLabel: "Save",
    compactTitle: "Correct dividend",
    // Gross, with withheld tax as the charge below — same split the entry form makes. Pre-PMA-325 dividends hold a net figure with no flag to tell them apart; re-presenting as gross is accepted since a backfill would have to guess at a tax nobody recorded.
    amountHelper:
      "The dividend declared, before any tax withheld at source. Any withheld tax is the field below.",
    // Written out rather than built from `chargeHelperFor`: a dividend's charge is withheld tax, not a commission.
    chargeHelper:
      "The tax withheld at source on this dividend. Clearing it removes the tax from the dividend.",
    headerNoun: "trade",
    contextHint: TRADE_CONTEXT_HINT,
    discardTitle: "Cancel this correction?",
  },
  FEE: {
    eyebrow: "CORRECT FEE",
    ariaLabel: "Correct fee form",
    submitLabel: "Save changes",
    compactActionLabel: "Save",
    compactTitle: "Correct fee",
    amountHelper: "The commission or charge debited from the account.",
    chargeHelper: chargeHelperFor("fee"),
    headerNoun: "trade",
    contextHint: TRADE_CONTEXT_HINT,
    discardTitle: "Cancel this correction?",
  },
  WITHHOLDING_TAX: genericCorrectionCopy(
    "withholding tax",
    "The tax withheld at source on this position's income.",
  ),
  CASH_IN_LIEU: genericCorrectionCopy(
    "cash in lieu",
    "The cash received for the fractional shares surrendered.",
  ),
  DEPOSIT: genericCorrectionCopy(
    "deposit",
    "The cash paid into this brokerage account.",
  ),
  WITHDRAWAL: genericCorrectionCopy(
    "withdrawal",
    "The cash taken out of this brokerage account.",
  ),
  ACCOUNT_FEE: accountLevelCorrectionCopy(
    "account fee",
    "The charge debited from this brokerage account.",
  ),
  INTEREST: {
    ...accountLevelCorrectionCopy(
      "interest",
      "The interest earned on, or charged to, this brokerage account's cash, before any tax withheld at source.",
    ),
    chargeHelper:
      "The tax withheld at source on this interest. Clearing it removes the tax from the interest.",
    directionGroupLabel: INTEREST_DIRECTION_GROUP_LABEL,
  },
  ADJUSTMENT: {
    ...accountLevelCorrectionCopy(
      "adjustment",
      "The residual the broker settled against this account's cash.",
    ),
    directionGroupLabel: ADJUSTMENT_DIRECTION_GROUP_LABEL,
  },
  INTEREST_TAX: accountLevelCorrectionCopy(
    "interest tax",
    "The tax withheld at source on this account's interest.",
  ),
  CASHBACK: accountLevelCorrectionCopy(
    "cashback",
    "The cashback credited to this brokerage account's cash.",
  ),
};

// Appended for an imported command: the gate the correction trips is account-wide and has no reset, so the panel says so before anything is saved.
const IMPORT_CONTEXT_HINT =
  "This entry came from a broker import. You can correct it here, but saving makes it your own record and permanently stops further statement imports into this account.";

export const correctionCopyFor = (
  command: TradeCommand,
): TradeCorrectionCopy => {
  const copy = TRADE_CORRECTION_COPY[command.type];
  if (command.commandOrigin !== "IMPORT") {
    return copy;
  }
  return {
    ...copy,
    contextHint: `${copy.contextHint} ${IMPORT_CONTEXT_HINT}`,
  };
};

// Mirrors buildReplacementDrafts exactly: a field the server would answer with a 400 is never rendered.
export const acceptsQuantity = (command: TradeCommand): boolean =>
  isSettlementType(command.type);

// Direction is the form's, not the command's: tax is withheld only on earned interest, so flipping it to charged drops the field.
export const acceptsCharge = (
  command: TradeCommand,
  direction: InterestDirection,
): boolean =>
  isSettlementType(command.type) ||
  command.type === "DIVIDEND" ||
  (command.type === "INTEREST" && direction === "EARNED");

export const taxedIncomeOf = (command: TradeCommand): TaxedIncome =>
  command.type === "INTEREST" ? "interest" : "dividend";

export const acceptsDirection = (command: TradeCommand): boolean =>
  command.type === "INTEREST" || command.type === "ADJUSTMENT";

// Read from chargeKind, never from type === "DIVIDEND": older dividend records may carry a fee, and their classification stays visible even though editing that shape is no longer permitted.
// null is the only case this panel decides itself — no kind can answer it, and on a dividend a correction can only ever write a tax leg.
export const chargeOf = (command: TradeCommand): ChargeNoun => {
  if (command.chargeKind === null) {
    return isSettlementType(command.type) ? "fee" : "withholdingTax";
  }
  return CHARGE_KIND_TO_NOUN[command.chargeKind];
};

// Spelled out rather than upper-casing the wire value, which would read `BUY_SETTLEMENT`.
const TRADE_CONTEXT_LABELS: Record<TradeCommandType, string> = {
  BUY_SETTLEMENT: "BUY",
  SELL_SETTLEMENT: "SELL",
  DIVIDEND: "DIVIDEND",
  WITHHOLDING_TAX: "TAX",
  FEE: "FEE",
  CASH_IN_LIEU: "CASH IN LIEU",
  DEPOSIT: "DEPOSIT",
  WITHDRAWAL: "WITHDRAWAL",
  ACCOUNT_FEE: "ACCOUNT FEE",
  INTEREST: "INTEREST",
  ADJUSTMENT: "ADJUSTMENT",
  INTEREST_TAX: "INTEREST TAX",
  CASHBACK: "CASHBACK",
};

export const buildContextLine = (command: TradeCommand): string =>
  [
    TRADE_CONTEXT_LABELS[command.type],
    // An account entry legitimately names no position, so the segment falls back rather than blanks.
    command.instrument?.symbol ?? ACCOUNT_LEVEL_POSITION,
    formatDate(command.effectiveDate, DateFormat.DMY_SPACE),
  ].join(" · ");

export const chargeFieldOf = (command: TradeCommand): ChargeFieldName =>
  CHARGE_FIELD_NAMES[chargeOf(command)];

// Defaults come from the command itself: an empty fee means no fee, which the PATCH then encodes by omitting the key.
export const createDefaultCorrection = (
  command: TradeCommand,
): TradeCorrectionFormValues => ({
  quantity: command.quantity ?? "",
  amountEur: command.amountEur,
  feeEur: "",
  taxWithheld: "",
  [chargeFieldOf(command)]: command.feeEur ?? "",
  // Preserve the stored sign while all correction amounts remain positive magnitudes.
  direction: command.moneyDirection === "OUT" ? "CHARGED" : "EARNED",
  // Parsed as a calendar date: `new Date("YYYY-MM-DD")` is UTC and becomes the
  // previous local day west of Greenwich.
  date: parseISO(command.effectiveDate),
  note: command.note ?? "",
  ...recordedCashbackValuesOf(command.cashback),
});

export const acceptsCashback = (command: TradeCommand): boolean =>
  command.type === "BUY_SETTLEMENT";

// Interest is refused on a linked broker, so it settles nothing.
export const correctionNetCentsOf = (
  command: TradeCommand,
  values: Pick<
    TradeCorrectionFormValues,
    "amountEur" | "feeEur" | "taxWithheld" | "direction" | "cashbackEur"
  >,
): number => {
  if (command.type === "INTEREST") {
    return 0;
  }
  const isCredit = acceptsDirection(command)
    ? values.direction === "EARNED"
    : command.moneyDirection === "IN";
  return settlementNetCentsOf({
    amountCents: centsOf(values.amountEur),
    chargeCents: acceptsCharge(command, values.direction)
      ? centsOf(values[chargeFieldOf(command)])
      : 0,
    direction: isCredit ? "IN" : "OUT",
    cashbackCents: acceptsCashback(command) ? centsOf(values.cashbackEur) : 0,
  });
};
